import { Link } from "react-router-dom";
import { Section } from "@/components/v2/blocks";
import { FinalCTA } from "@/components/v2/blocks";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Quote, ArrowRight, Check, FileText, ShieldCheck } from "lucide-react";
import WhyBento from "@/components/v2/WhyBento";
import HowSteps from "@/components/v2/HowSteps";
import TraceCard from "@/components/v2/TraceCard";
import PriceTicker from "@/components/v2/PriceTicker";
import Seo from "@/components/v2/Seo";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";

const FAQ = [
  ["L'étude est-elle gratuite ?", "Oui. L'étude initiale est réalisée sans frais et sans engagement."],
  ["Puis-je transmettre uniquement ma facture ?", "Oui. Votre facture contient déjà une grande partie des informations utiles à une première analyse."],
  ["Travaillez-vous avec les particuliers ?", "Oui, Energy Market accompagne les particuliers partout en France."],
  ["Travaillez-vous avec les professionnels ?", "Oui, des artisans et commerçants aux PME, groupes et syndics de copropriété."],
  ["L'ACD engage-t-elle mon entreprise ?", "Non. L'Autorisation de Collecte de Données n'est pas un contrat et n'entraîne aucun changement de fournisseur."],
];

const ARTICLES = [
  { t: "Comment anticiper l'échéance d'un contrat d'énergie ?", c: "Conseils professionnels" },
  { t: "Comprendre sa facture d'électricité", c: "Conseils particuliers" },
  { t: "Pourquoi certains fournisseurs demandent-ils une ACD ?", c: "Réglementation" },
];

const SUPPLIER_LOGOS = ["edf", "engie", "totalenergies", "octopus", "eni", "ekwateur", "mint", "ilek"];

export default function APropos() {
  return (
    <div>
      <Seo title="À propos" description="Energy Market, courtier en énergie indépendant fondé par Killian Guivarch. Accompagnement clair et personnalisé, partout en France." path="/a-propos" />
      <PageHeroPhoto
        cmsKey="apropos"
        eyebrow="À propos"
        title="À propos d'Energy Market"
        subtitle="Energy Market est né d'une volonté simple : rendre le marché de l'énergie plus accessible et plus compréhensible, pour les particuliers comme pour les professionnels."
        image="/img/hero-a-propos.png"
        overlay="brand-light"
        cta={false}
        parallax="fixed"
        contentBottom
        highlightsLabel="En bref"
        highlights={[
          "Plus de 9 ans d'expertise sur le marché de l'énergie",
          "Une approche indépendante, sans parti pris",
          "Pour les particuliers et les professionnels",
          "Un accompagnement clair, partout en France",
        ]}
      />


      {/* TICKER PRIX ÉNERGIE — données CRE temps réel */}
      <PriceTicker />

      {/* BARRE DE CONFIANCE — fournisseurs comparés (défilement continu) */}
      <section className="bg-cream border-b border-border py-10">
        <div className="em-container">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-7">
            Nous comparons les principaux fournisseurs du marché
          </p>
        </div>
        <div className="em-marquee">
          <div className="em-marquee__track">
            {[...Array(4)].flatMap(() => SUPPLIER_LOGOS).map((s, i) => (
              <img
                key={`${s}-${i}`}
                src={`/img/suppliers/${s}.png`}
                alt={i < SUPPLIER_LOGOS.length ? s : ""}
                aria-hidden={i >= SUPPLIER_LOGOS.length}
                loading="lazy"
                className="h-7 md:h-8 w-auto object-contain mx-8 md:mx-10 shrink-0 opacity-60 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0"
              />
            ))}
          </div>
        </div>
      </section>

      {/* À QUI ÇA S'ADRESSE — cartes photo plein cadre (style capeo) */}
      <section className="bg-secondary">
        <div className="em-container em-section">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-primary max-w-3xl">À qui s'adresse Energy Market ?</h2>
          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {[
              { to: "/professionnels", img: "hero-b2b", eyebrow: "Energy Market pour les pros", title: "Professionnel", text: "Optimisation des contrats, anticipation des échéances, gestion multi-sites et copropriétés.", bullets: ["Optimisation & renégociation", "Gestion multi-sites & copropriétés", "Analyse de vos factures"], cta: "Solutions professionnels" },
              { to: "/particuliers", img: "hero-b2c", eyebrow: "Energy Market pour les particuliers", title: "Particulier", text: "Comparer les offres, changer de fournisseur, préparer un déménagement, comprendre sa facture.", bullets: ["Comparaison des offres", "Changement de fournisseur simplifié", "Accompagnement déménagement"], cta: "Solutions particuliers" },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="group relative flex min-h-[440px] items-end overflow-hidden rounded-[2rem]">
                <picture>
                  <source srcSet={`/img/${c.img}.webp`} type="image/webp" />
                  <img
                    src={`/img/${c.img}.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </picture>
                <div className="absolute inset-0 bg-primary/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/65 to-transparent" />
                <div className="relative p-8 md:p-10">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{c.eyebrow}</p>
                  <h3 className="mt-2 font-display text-4xl md:text-5xl font-black text-white">{c.title}</h3>
                  <p className="mt-3 max-w-sm leading-relaxed text-white/85">{c.text}</p>
                  <ul className="mt-4 space-y-2">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-white/90">
                        <Check className="h-4 w-4 shrink-0 text-accent" /> {b}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/50 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 group-hover:bg-white group-hover:text-primary">
                    {c.cta} <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* POURQUOI — grille bento */}
      <WhyBento />

      {/* FONDATEUR — panneau premium avec photo */}
      <section className="bg-background">
        <div className="em-container em-section">
          <div className="grid lg:grid-cols-12 overflow-hidden rounded-[2rem] bg-primary text-primary-foreground">
            <div className="relative lg:col-span-5 min-h-[300px] lg:min-h-[480px]">
              <picture>
                <source srcSet="/img/killian-guivarch.webp" type="image/webp" />
                <img
                  src="/img/killian-guivarch.jpg"
                  alt="Killian Guivarch, fondateur d'Energy Market"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-primary/50" />
            </div>
            <div className="lg:col-span-7 flex flex-col justify-center p-8 md:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Une expertise construite sur le terrain</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-black leading-tight">Killian Guivarch, fondateur d'Energy Market</h2>
              <p className="mt-5 leading-relaxed text-primary-foreground/80">
                Depuis plus de neuf ans, Killian Guivarch accompagne des professionnels dans la gestion et l'analyse de leurs contrats d'électricité et de gaz. Une vision concrète du marché, fondée sur l'écoute, la pédagogie et la proximité.
              </p>
              <div className="mt-6 border-l-2 border-accent pl-5">
                <Quote className="h-6 w-6 text-accent" aria-hidden />
                <p className="mt-2 font-display text-lg md:text-xl font-semibold leading-snug">
                  « Chaque consommateur doit pouvoir comprendre son environnement énergétique pour prendre des décisions éclairées. »
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <span className="rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium">9+ ans d'expérience</span>
                <span className="rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium">Particuliers &amp; professionnels</span>
                <span className="rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium">Partout en France</span>
              </div>
              <Button asChild className="mt-7 w-fit rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                <Link to="/a-propos">Découvrir Energy Market <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT CA MARCHE */}
      <Section eyebrow="Comment ça fonctionne ?" title="Un parcours simple, en quatre étapes" muted>
        <HowSteps />
      </Section>

      {/* DEPOT FACTURE — texte + mini-facture */}
      <section className="bg-background">
        <div className="em-container em-section">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="em-eyebrow mb-3">Déposez votre facture</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-primary">Votre facture contient déjà l'essentiel</h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Fournisseur actuel, caractéristiques du contrat, consommation, références techniques… Transmettre votre facture
                permet une analyse plus rapide et plus précise.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Formats acceptés :</span>
                {["PDF", "JPG", "PNG"].map((f) => (
                  <span key={f} className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary">{f}</span>
                ))}
              </div>
              <Button asChild className="mt-7 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                <Link to="/contact?type=facture"><FileText className="mr-2 h-4 w-4" /> Déposer ma facture</Link>
              </Button>
              <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden /> Vos données restent confidentielles et servent uniquement à votre étude.
              </p>
            </div>

            <div className="relative">
              <div className="rounded-3xl border border-border bg-card p-6 shadow-2xl">
                <div className="flex items-center gap-2.5 border-b border-border pb-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white"><FileText className="h-4 w-4" aria-hidden /></div>
                  <div>
                    <p className="text-sm font-bold text-primary">Facture d'électricité</p>
                    <p className="text-[11px] text-muted-foreground">Période · 12 mois</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    ["Fournisseur actuel", "EDF — Tarif Bleu", true],
                    ["Consommation annuelle", "4 200 kWh", true],
                    ["Point de livraison (PDL)", "14 5•• •••", false],
                    ["Prix du kWh", "0,2516 €", true],
                    ["Échéance du contrat", "12/2025", false],
                  ].map(([label, value, hot]) => (
                    <div key={label} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${hot ? "bg-accent/20 ring-1 ring-accent/40" : ""}`}>
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-semibold text-primary tabular-nums">{value}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="inline-block h-2 w-2 rounded-sm bg-accent" /> Les éléments clés que nous analysons pour vous.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VEILLE */}
      <Section eyebrow="Veille Marché" title="L'actualité de l'énergie, décryptée" intro={<span className="lg:whitespace-nowrap">Retrouvez régulièrement nos analyses, actualités et conseils afin de mieux comprendre les évolutions du secteur.</span>}>
        <div className="grid gap-5 md:grid-cols-3">
          {ARTICLES.map((a, i) => {
            const color = ["#2E7D62", "#7AB317", "#0E9C8A"][i % 3];
            return (
              <TraceCard key={a.t} index={i} radius={16} accent={color} className="flex flex-col rounded-2xl border border-border bg-card p-6">
                <span className="relative w-fit rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: `${color}1A`, color }}>{a.c}</span>
                <h3 className="relative mt-4 font-display text-lg font-bold leading-snug text-primary">{a.t}</h3>
                <Link to="/veille-marche" className="relative mt-auto inline-flex items-center gap-1 pt-5 text-sm font-semibold text-primary transition-all group-hover:gap-2">
                  Lire l'article <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </TraceCard>
            );
          })}
        </div>
        <div className="mt-8">
          <Button asChild variant="outline" className="rounded-full"><Link to="/veille-marche">Accéder à la Veille Marché</Link></Button>
        </div>
      </Section>

      {/* FAQ */}
      <Section eyebrow="Questions fréquentes" title="Vous vous posez sûrement ces questions" muted>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger>{q}</AccordionTrigger>
                <AccordionContent>{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8">
            <Button asChild variant="outline" className="rounded-full"><Link to="/faq">Consulter la FAQ complète</Link></Button>
          </div>
        </div>
      </Section>

      <FinalCTA title="Parlons de votre énergie" text="Une question, une facture à déposer ou une étude personnalisée ? Nous sommes à votre disposition." />
    </div>
  );
}
